import { useState } from 'react';
import { Box, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * "See it in your room": the crowns, enlarged, standing on the patient's own
 * table through their phone's camera.
 *
 * Google's model-viewer does the AR - Quick Look on an iPhone, WebXR or Scene
 * Viewer on Android - and it is loaded only when this button is pressed, so no
 * patient who never taps it downloads it. The model is exported from the scene
 * already on screen, so it carries the same shade and needs no extra file from
 * the server.
 *
 * On a computer there is no camera to stand it in front of; the button says
 * to open the page on a phone instead.
 */
interface ModelViewerElement extends HTMLElement {
  canActivateAR: boolean;
  activateAR: () => Promise<void>;
}

interface ARButtonProps {
  label: string;
  unsupported: string;
  getModel: () => Promise<ArrayBuffer | null>;
  className?: string;
}

export function ARButton({ label, unsupported, getModel, className }: ARButtonProps) {
  const [busy, setBusy] = useState(false);

  const open = async () => {
    // A computer has no camera to stand the crown in front of, so there is
    // nothing to load: say so straight away.
    if (!window.matchMedia('(pointer: coarse)').matches) {
      toast.info(unsupported);
      return;
    }
    setBusy(true);
    let url: string | null = null;
    let viewer: ModelViewerElement | null = null;
    try {
      const [model] = await Promise.all([getModel(), import('@google/model-viewer')]);
      if (!model) return;
      url = URL.createObjectURL(new Blob([model], { type: 'model/gltf-binary' }));

      viewer = document.createElement('model-viewer') as ModelViewerElement;
      viewer.setAttribute('src', url);
      viewer.setAttribute('ar', '');
      viewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
      viewer.setAttribute('ar-placement', 'floor');
      // model-viewer otherwise waits until it scrolls into view, which an
      // element parked off screen never does.
      viewer.setAttribute('loading', 'eager');
      // Off screen, not display:none - a hidden element never loads its model.
      Object.assign(viewer.style, {
        position: 'fixed',
        left: '-10000px',
        width: '2px',
        height: '2px',
      });
      document.body.appendChild(viewer);

      await new Promise<void>((resolve, reject) => {
        window.setTimeout(() => {
          reject(new Error('model-viewer took too long to load the crowns'));
        }, 20_000);
        viewer!.addEventListener(
          'load',
          () => {
            resolve();
          },
          { once: true },
        );
        viewer!.addEventListener(
          'error',
          () => {
            reject(new Error('model-viewer could not load the crowns'));
          },
          { once: true },
        );
      });

      if (viewer.canActivateAR) await viewer.activateAR();
      else toast.info(unsupported);
    } catch (error) {
      console.error(error);
      toast.info(unsupported);
    } finally {
      setBusy(false);
      // The AR session keeps its own copy once started.
      window.setTimeout(() => {
        viewer?.remove();
        if (url) URL.revokeObjectURL(url);
      }, 60_000);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void open()}
      disabled={busy}
      className={cn(
        'flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground shadow-md transition-opacity hover:opacity-90 disabled:opacity-70',
        className,
      )}
    >
      {busy ? <Loader2 className="size-4 animate-spin" /> : <Box className="size-4" />}
      {label}
    </button>
  );
}
